import { TvGuideChannel, TvGuideProgram } from '@smart-tv/pages/tv-guide/types';

import defaultSeriesCover from '@smart-tv/scaffold/images/placeholder_default_landscape.svg';

const emptyProgram: TVGuideProgramExtended = {
  imageUrl: defaultSeriesCover,
  sid: 999999,
  eventId: 999999,
  title: 'Ingen programinformation',
  period: '',
  startTime: 0,
  endTime: 0,
  startTimeInMinutes: 0,
  endTimeInMinutes: 1440,
  duration: 86400,
  durationInMinutes: 1440,
  isFavorite: false,
  catchupTimeout: 0,
  isLastProgram: true,
  trickModes: 0,
  genre: '',
  year: '',
  recording: false,
  inactive: true
};

export interface TVGuideProgramExtended extends TvGuideProgram {
  inactive: boolean;
}

/*
  Loop through every channel and every program to check if there is a gap between programs (missing data)
  Create an empty inactive program to make sure that there are no holes in the TV-guide data
*/
export const createEmptyPrograms = (epgChannels: TvGuideChannel[] | undefined) => {
  if (epgChannels) {
    let eventIdCount = 0;

    epgChannels.forEach((channel) => {
      if (channel.programs.length > 0) {
        let previousProgramEndTimeInMinutes = 0;
        const newChannelPrograms = [...channel.programs];

        channel.programs.forEach((program, pIndex, array) => {
          const firstProgramMissing = pIndex === 0 && program.startTimeInMinutes !== 0;
          const middleProgramMissing =
            previousProgramEndTimeInMinutes !== program.startTimeInMinutes;
          const lastProgramMissing =
            pIndex === array.length - 1 && program.endTimeInMinutes !== 1440;

          if (firstProgramMissing || middleProgramMissing || lastProgramMissing) {
            const emptyP = { ...emptyProgram };
            emptyP.eventId = emptyP.eventId + eventIdCount;

            const prevProgram = firstProgramMissing
              ? emptyProgram
              : middleProgramMissing
              ? channel.programs[pIndex - 1]
              : program;

            const nextProgram = lastProgramMissing ? emptyProgram : program;

            emptyP.sid = channel.sid;
            if (firstProgramMissing) {
              emptyP.period = '00:00 - ' + nextProgram.period.split('-')[0];
              emptyP.startTime = nextProgram.startTime - nextProgram.startTimeInMinutes * 60;
              emptyP.endTime = nextProgram.startTime;
              emptyP.endTimeInMinutes = nextProgram.startTimeInMinutes;
              emptyP.duration = emptyP.endTime - emptyP.startTime;
              emptyP.durationInMinutes = (emptyP.endTime - emptyP.startTime) / 60;
              emptyP.isLastProgram = false;
            } else if (middleProgramMissing) {
              emptyP.period =
                prevProgram.period.split('-')[1] + ' - ' + program.period.split('-')[0];
              emptyP.startTime = prevProgram.endTime;
              emptyP.endTime = nextProgram.startTime;
              emptyP.startTimeInMinutes = prevProgram.endTimeInMinutes;
              emptyP.endTimeInMinutes = nextProgram.startTimeInMinutes;
              emptyP.duration = nextProgram.startTime - prevProgram.endTime;
              emptyP.durationInMinutes = (nextProgram.startTime - prevProgram.endTime) / 60;
              emptyP.isLastProgram = false;
            } else if (lastProgramMissing) {
              emptyP.period = prevProgram.period.split('-')[1] + ' - 00:00';
              emptyP.startTime = prevProgram.endTime;
              emptyP.startTimeInMinutes = prevProgram.endTimeInMinutes;
              emptyP.endTime = prevProgram.endTime + (1440 - prevProgram.endTimeInMinutes) * 60;
              emptyP.duration = emptyP.endTime - emptyP.startTime;
              emptyP.durationInMinutes = 1440 - prevProgram.endTimeInMinutes;
            }

            const insertIndex = lastProgramMissing ? 1 : 0;
            newChannelPrograms.splice(pIndex + insertIndex, 0, emptyP);

            eventIdCount++;
          }
          previousProgramEndTimeInMinutes = program.endTimeInMinutes;
        });
        channel.programs = newChannelPrograms;
      } else {
        emptyProgram.eventId = emptyProgram.eventId + eventIdCount;
        channel.programs = [emptyProgram];
        eventIdCount++;
      }
    });
  }

  return epgChannels;
};
